import {ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon} from 'lucide-react';
import * as React from 'react';
import type {DayButton} from 'react-day-picker';
import {DayPicker, getDefaultClassNames} from 'react-day-picker';

import {Button} from '@/components/ui/button';
import {iconButtonVariants} from '@/components/ui/icon-button/icon-button-variants';
import {cn} from '@/utils/ui';

import {calendarDayVariants} from './calendar-variants';

function Calendar({
  className,
  classNames,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      className={cn(
        'group/calendar bg-transparent p-3 [--cell-size:2.25rem]',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: date =>
          date.toLocaleString('default', {month: 'short'}),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          iconButtonVariants({variant: 'tertiary', size: 'small'}),
          'select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          iconButtonVariants({variant: 'tertiary', size: 'small'}),
          'select-none aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 type-body2 font-medium text-secondary-900',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'relative rounded-sharp border border-input bg-white shadow-subtle has-focus:border-secondary-400 has-focus:ring-2 has-focus:ring-ring has-focus:ring-offset-2',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          'bg-popover absolute inset-0 opacity-0',
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          'select-none font-urbanist font-medium text-secondary-900',
          captionLayout === 'label'
            ? 'type-btn-sm'
            : 'flex h-8 items-center gap-1 rounded-sharp px-3 type-body2 [&>svg]:size-3.5 [&>svg]:text-secondary-700',
          defaultClassNames.caption_label
        ),
        month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 select-none rounded-full py-1 text-center text-xs font-semibold text-neutral-500',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn(
          'w-(--cell-size) select-none',
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          'text-muted-foreground select-none text-[0.8rem]',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full select-none p-0 text-center',
          defaultClassNames.day
        ),
        range_start: cn(
          'rounded-l-full bg-secondary-50',
          defaultClassNames.range_start
        ),
        range_middle: cn(
          'rounded-none bg-secondary-50',
          defaultClassNames.range_middle
        ),
        range_end: cn(
          'rounded-r-full bg-secondary-50',
          defaultClassNames.range_end
        ),
        today: cn(
          'rounded-full data-[selected=true]:rounded-none',
          defaultClassNames.today
        ),
        outside: cn(
          'text-neutral-400 aria-selected:text-neutral-400',
          defaultClassNames.outside
        ),
        disabled: cn('text-neutral-300 opacity-60', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({className, rootRef, ...props}) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({className, orientation, ...props}) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon className={cn('size-4', className)} {...props} />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-4', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn('size-4', className)} {...props} />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({children, ...props}) => {
          return (
            <td {...props}>
              <div className="flex h-full w-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="tertiary"
      size="text"
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today}
      data-outside={modifiers.outside}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        calendarDayVariants(),
        'data-[outside=true]:text-neutral-400',
        'data-[today=true]:font-bold data-[today=true]:text-secondary-900',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export {Calendar, CalendarDayButton};
